import * as React from 'react';
import Icons from '@/const/icons';
import { CardContainer } from '../containers';
import { Spinner, TypographyType } from '@/components/common';
import { EmptyStateCaseDetails } from './EmptyStateCaseDetails';
import { ActionRequired, StatusTypes } from '@/interfaces';
import { ActionRequiredItem } from './ActionRequiredItem';
import { GuidedTourModal } from '@/components/modals/guided-tour/GuidedTourModal';
import { useActionRequiredActions } from '@/hooks/useActionRequiredActions';
import { useActionRequiredOnboarding } from '@/hooks/useActionRequiredOnboarding';
import { ActionRequiredModals } from './action-required/ActionRequiredModals';
import { ActionRequiredWelcomeModal } from './action-required/ActionRequiredWelcomeModal';
import { ActionRequiredOnboardingItems } from './action-required/ActionRequiredOnboardingItems';

interface Props {
	caseName?: string;
	applicationId?: number;
	status?: StatusTypes;
	className?: string;
	withTitle?: boolean;
	withIcon?: boolean;
	classNameContent?: string;
	actionRequired?: ActionRequired[];
	dateTypography?: TypographyType;
	isDashboard?: boolean;
	clientApproveEmail?: boolean;
	classNameContainer?: string;
	stepName?: string;
	numericalOrder?: number;
	emptyMessage?: string;
	refecthDocuments?: () => void;
	refetchCase?: () => void;
	refecthActionRequired?: () => void;
}

export const ActionRequiredComponent: React.FC<Props> = ({
	caseName,
	applicationId,
	status,
	className,
	withTitle,
	withIcon = false,
	classNameContent,
	actionRequired,
	dateTypography = 'regular-16',
	isDashboard,
	clientApproveEmail,
	stepName,
	numericalOrder,
	refetchCase,
	emptyMessage = 'You have no pending actions.',
	refecthDocuments,
	refecthActionRequired,
	classNameContainer,
}) => {
	const actions = useActionRequiredActions({
		actionRequired,
		numericalOrder,
		refetchers: {
			refetchActionRequired: refecthActionRequired,
			refetchCase,
			refetchDocuments: refecthDocuments,
		},
	});
	const onboarding = useActionRequiredOnboarding({ isDashboard, actions });

	const {
		actionRequiredList,
		visibleActionRequiredList,
		showVerifyIdentityAction,
		hasDocumentAction,
		handleClickOptionToOpen,
		selectActionRequired,
		openStandaloneVerification,
	} = actions;
	const { showTakeTheTourAction } = onboarding;

	return (
		<>
			<CardContainer
				title="Action Required"
				icon={Icons.infoEmptyState}
				status={status}
				stepName={stepName}
				clientApproveEmail={clientApproveEmail}
				classNameContent={classNameContent}
				className={classNameContainer}
				classNameTitle="!text-xl"
			>
				{actionRequiredList ? (
					showVerifyIdentityAction ||
					showTakeTheTourAction ||
					visibleActionRequiredList.length > 0 ? (
						<ul className={className}>
							<ActionRequiredOnboardingItems
								showVerifyIdentityAction={showVerifyIdentityAction}
								showTakeTheTourAction={showTakeTheTourAction}
								onVerifyIdentity={openStandaloneVerification}
								onTakeTheTour={onboarding.handleTakeTheTourClick}
							/>
							{visibleActionRequiredList.map((item) => (
								<React.Fragment key={item.id}>
									<ActionRequiredItem
										dateTypography={dateTypography}
										item={item}
										withTitle={withTitle}
										withIcon={withIcon}
										isDashboard={isDashboard}
										setActionRequiredSelected={selectActionRequired}
										handleClickOptionToOpen={handleClickOptionToOpen}
									/>
								</React.Fragment>
							))}
						</ul>
					) : (
						<EmptyStateCaseDetails text={emptyMessage} />
					)
				) : (
					<div className="flex justify-center items-center w-full h-full">
						<Spinner type="loadingPage" />
					</div>
				)}
			</CardContainer>

			<ActionRequiredWelcomeModal
				Modal={onboarding.WelcomeModal}
				hasDocumentAction={hasDocumentAction}
				onDismiss={onboarding.dismissWelcome}
				onStartVerification={onboarding.handleStartVerificationFromWelcome}
			/>

			<ActionRequiredModals
				actions={actions}
				applicationId={applicationId}
				caseName={caseName}
			/>

			{showTakeTheTourAction && (
				<GuidedTourModal
					Modal={onboarding.ModalGuidedTour}
					onClose={onboarding.hideGuidedTour}
					onFinish={onboarding.hideGuidedTour}
				/>
			)}
		</>
	);
};
